import { useEffect, useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { SideDrawer } from '@/components';
import { Modal } from '@/components/Elements/Modal';
import {
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  TableChip,
  getConsentChipTone,
} from '../../components/PremiumDesign';
import {
  importCRMContacts,
  getGoogleWorkspaceStatus,
  getGoogleWorkspaceAuthUrl,
  importGoogleWorkspaceSheet,
  getGoogleWorkspacePickerSession,
} from '../api';
import * as XLSX from 'xlsx';
import { formatConsentStatus } from '@/common/utils';
import { GoogleSheetsIcon, GoogleDriveIcon } from './GoogleSheetSelector';
import {
  loadGoogleApiScript,
  openGoogleAuthWindow,
  openSpreadsheetPicker,
  parseGoogleWorkspaceError,
  pickerSessionErrorMessage,
} from '../utils/googleWorkspaceUtils';

const GW_CALLBACK_URL = `${typeof window !== 'undefined' ? window.location.origin : ''}/integrations/google-workspace/callback`;

const PREVIEW_ROWS = Array.from({ length: 4 }, (_, i) => ({
  name: 'John Uber',
  email: 'flexioninfotech136@gmail.com',
  phone: '+91 12345 67890',
  consent: i % 2 === 0 ? 'Unknown' : 'Opt_in',
  joined: '02 Jul 2026, 10:30 AM',
}));

function FileDocIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="8" y1="9" x2="10" y2="9" />
    </svg>
  );
}

export function CRMImportWizard({
  isOpen,
  onClose,
  companyId,
  onImportSuccess,
}) {
  // Selected file/sheet state
  const [file, setFile] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetName, setSheetName] = useState('Sheet1');
  const [sheetPreview, setSheetPreview] = useState(null);
  const [isReadyToImport, setIsReadyToImport] = useState(false);
  const [isSheetLoading, setIsSheetLoading] = useState(false);
  const [uploadSource, setUploadSource] = useState(null); // 'device' | 'google_drive'

  const [isPickerLoading, setIsPickerLoading] = useState(false);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Import states & results
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [fileImportSummary, setFileImportSummary] = useState(null);

  // 1. Google Workspace Connection Query
  const { data: gwStatus, refetch: refetchGwStatus } = useQuery({
    queryKey: ['google-workspace-status', companyId],
    queryFn: () => getGoogleWorkspaceStatus(companyId),
    enabled: isOpen && !!companyId,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    const onAuthDone = (event) => {
      if (event.data?.type !== 'gw-auth-done') return;
      if (event.data.status === 'error') {
        toast.error(event.data.message || 'Google sign-in did not finish.');
        return;
      }
      if (event.data.status !== 'success') return;
      refetchGwStatus();
    };
    const onFocus = () => {
      refetchGwStatus();
    };
    window.addEventListener('message', onAuthDone);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('message', onAuthDone);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchGwStatus]);

  const isGwConnected = Boolean(gwStatus?.is_connected);

  const handleClose = () => {
    setFile(null);
    setSelectedSheet(null);
    setSheetName('Sheet1');
    setUploadSource(null);
    onClose();
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      {
        name: 'John Uber',
        email: 'john@gmail.com',
        phone: '+911234567890',
        consent_status: 'opt_in',
        joined_date: '02 Jul 2026',
      },
      {
        name: 'Hardik Navadiya',
        email: 'hardik@gmail.com',
        phone: '+911234567890',
        consent_status: 'opt_out',
        joined_date: '02 Jul 2026',
      },
      {
        name: 'Anjali Chaudhary',
        email: 'anjali@gmail.com',
        phone: '+911234567890',
        consent_status: 'unknown',
        joined_date: '02 Jul 2026',
      },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
    XLSX.writeFile(wb, 'crm_sample_contacts.xlsx');
  };

  // 1. Device File Import Mutation
  const { mutate: handleFileImport, isPending: isFileImporting } = useMutation({
    mutationFn: importCRMContacts,
    onSuccess: (data) => {
      if (data.status === 'pending' || data.status === 'processing') {
        toast.success(
          `Import started for ${data.file_name}. ${data.total_rows} rows are being processed in the background.`,
        );
      } else {
        setFileImportSummary(normalizeImportSummary(data));
      }
      if (onImportSuccess) onImportSuccess();
      handleClose();
    },
    onError: (err) => {
      const errorData = err.response?.data;
      toast.error(
        errorData?.message || errorData?.detail || 'Failed to import contacts.',
      );
    },
  });

  // 2. Google Sheets Import Mutation
  const { mutate: handleSheetsImport, isPending: isSheetsImporting } =
    useMutation({
      mutationFn: importGoogleWorkspaceSheet,
      onSuccess: (data) => {
        setFileImportSummary(normalizeImportSummary(data));
        if (onImportSuccess) onImportSuccess();
        handleClose();
      },
      onError: (err) => {
        const parsed = parseGoogleWorkspaceError(err);
        toast.error(parsed.message);
        if (parsed.isAuthError) {
          refetchGwStatus();
        }
      },
    });

  // Google OAuth Flow Trigger
  const handleConnectGoogle = async () => {
    if (!companyId) {
      toast.error('Company ID is missing.');
      return;
    }
    setIsConnectingGoogle(true);
    try {
      const res = await getGoogleWorkspaceAuthUrl(companyId, GW_CALLBACK_URL);
      const authUrl = res?.auth_url ?? res?.url;
      if (!authUrl) {
        toast.error('Could not retrieve Google authorization URL.');
        setIsConnectingGoogle(false);
        return;
      }
      sessionStorage.setItem('gw_company_id', String(companyId));
      localStorage.setItem('gw_company_id', String(companyId));
      openGoogleAuthWindow(authUrl);
      setIsConnectingGoogle(false);
    } catch (err) {
      const parsed = parseGoogleWorkspaceError(err);
      toast.error(parsed.message || 'Failed to initiate Google sign-in.');
      setIsConnectingGoogle(false);
    }
  };

  // Google Picker Option
  const openGooglePicker = async () => {
    if (!companyId) {
      toast.error('Company ID is missing.');
      return;
    }
    const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
    if (!apiKey) {
      toast.error('VITE_GOOGLE_PICKER_API_KEY is missing.');
      return;
    }
    setIsPickerLoading(true);
    try {
      const session = await getGoogleWorkspacePickerSession(companyId);
      await loadGoogleApiScript();
      await openSpreadsheetPicker({
        accessToken: session.access_token,
        appId: session.app_id,
        apiKey,
        onPicked: (file) => {
          const tab = sheetName.trim() || 'Sheet1';
          setIsSheetLoading(true);
          setSelectedSheet({ id: file.id, name: file.name });
          setSheetPreview({
            fileName: file.name,
            sheetName: tab,
            rows: null,
          });
          setIsReadyToImport(true);
          setFile(null);
          setUploadSource('google_drive');
          setIsSheetLoading(false);
          toast.success(`Selected "${file.name}"`);
        },
      });
    } catch (err) {
      const sessionMessage = pickerSessionErrorMessage(err);
      if (sessionMessage) {
        toast.error(sessionMessage);
        refetchGwStatus();
      } else {
        toast.error(
          parseGoogleWorkspaceError(err).message ||
            'Could not open Google Picker.',
        );
      }
    } finally {
      setIsPickerLoading(false);
    }
  };

  // Device file chosen via native file picker
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setSelectedSheet(null);
      setSheetPreview(null);
      setIsReadyToImport(true);
      setIsSheetLoading(false);
      setUploadSource('device');

      try {
        const data = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      } catch (error) {
        console.error('Error parsing file:', error);
      }
    }
  };

  // Drag and Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const isValid = validExtensions.some((ext) =>
        droppedFile.name.toLowerCase().endsWith(ext),
      );

      if (!isValid) {
        toast.error('Please drop an Excel (.xlsx, .xls) or CSV file.');
        return;
      }

      setFile(droppedFile);
      setSelectedSheet(null);
      setSheetPreview(null);
      setIsReadyToImport(true);
      setIsSheetLoading(false);
      setUploadSource('device');

      try {
        const data = await droppedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      } catch (error) {
        console.error('Error parsing dropped file:', error);
      }
    }
  };

  const openDevicePicker = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const openDrivePicker = () => {
    if (!isGwConnected) {
      handleConnectGoogle();
      return;
    }
    openGooglePicker();
  };

  const clearSelection = (e) => {
    if (e) e.stopPropagation();
    setFile(null);
    setSelectedSheet(null);
    setSheetPreview(null);
    setIsReadyToImport(false);
    setUploadSource(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onStartImport = () => {
    if (uploadSource === 'google_drive' || selectedSheet) {
      if (!selectedSheet?.id) {
        toast.error('Please select a Google Spreadsheet to import.');
        return;
      }
      handleSheetsImport({
        companyId,
        payload: {
          spreadsheet_id: selectedSheet.id,
          sheet_name: sheetName.trim() || 'Sheet1',
        },
      });
    } else {
      if (!file) {
        toast.error('Please select a file to import.');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      handleFileImport({
        companyId,
        payload: formData,
      });
    }
  };

  const isImporting = isFileImporting || isSheetsImporting;
  const hasSelection = Boolean(file) || Boolean(selectedSheet?.id);
  const canSubmit =
    !isImporting &&
    (Boolean(file) ||
      (Boolean(selectedSheet?.id) && isReadyToImport && !isSheetLoading));

  return (
    <>
      {/* 1. Main Drawer */}
      <SideDrawer
        isOpen={isOpen}
        onClose={handleClose}
        width="xl"
        className="lead-import-drawer"
        zIndexClass="z-[60]"
        showClose={false}
        bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
        headerClassName="!px-4 !py-3.5 sm:!px-6 sm:!py-5"
        footerClassName="!px-4 !py-3.5 sm:!px-6 sm:!py-5"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <h2 className="text-[18px] sm:text-[20px] leading-[26px] sm:leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
              Import Contacts
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="flex justify-end w-full">
              <button
                type="button"
                disabled={!canSubmit}
                onClick={onStartImport}
                className="inline-flex h-[44px] items-center justify-center gap-[6px] rounded-[8px] px-5 text-[15px] font-medium text-white transition-colors focus:outline-none disabled:cursor-not-allowed w-full sm:w-auto shadow-sm"
                style={{
                  backgroundColor: canSubmit ? '#50AB3D' : '#A8D49E',
                }}
              >
                {isImporting ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <span>{hasSelection ? 'Import Contacts' : 'Submit'}</span>
                    <span aria-hidden className="text-lg leading-none">
                      →
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-4 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5 h-full min-h-0 font-sf-pro-display bg-white overflow-y-auto custom-scrollbar">
          {/* Instruction banner */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#F5F5F5] px-3.5 sm:px-4 py-3 sm:py-3.5 rounded-[8px] shrink-0">
            <p className="text-[13px] sm:text-[14px] leading-[20px] sm:leading-[22px] text-[#868686] flex-1 min-w-0">
              Please ensure that your file is in{' '}
              <span className="font-semibold text-[#1F2241]">
                Excel format (.xlsx, .xls)
              </span>{' '}
              or{' '}
              <span className="font-semibold text-[#1F2241]">CSV format</span>{' '}
              before importing.
            </p>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center justify-center gap-2 shrink-0 h-[40px] px-4 rounded-[8px] bg-[#50AB3D] text-white text-[13px] sm:text-[14px] font-medium hover:bg-[#2F8E29] transition-colors focus:outline-none whitespace-nowrap w-full sm:w-auto"
            >
              <FileDocIcon className="w-4 h-4" />
              Download Template
            </button>
          </div>

          {/* Expected Format Preview */}
          <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-hidden">
            <h3 className="text-[14px] sm:text-[15px] leading-[22px] font-semibold text-[#50AB3D] shrink-0">
              Expected Format Preview
            </h3>

            {/* Table + Upload share common #F5F5F5 background */}
            <div className="flex flex-col gap-4 bg-[#F5F5F5] rounded-[12px] p-3 sm:p-4 flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto custom-scrollbar">
                <DashboardTable>
                  <DashboardTableHeader>
                    <th className="!py-3 px-3 text-left !text-[13px] sm:!text-[13px]">
                      Name
                    </th>
                    <th className="!py-3 px-3 text-left !text-[13px] sm:!text-[13px]">
                      Email
                    </th>
                    <th className="!py-3 px-3 text-left !text-[13px] sm:!text-[13px]">
                      Phone
                    </th>
                    <th className="!py-3 px-3 text-left !text-[13px] sm:!text-[13px]">
                      Consent
                    </th>
                    <th className="!py-3 px-3 text-left !text-[13px] sm:!text-[13px]">
                      Joined Date
                    </th>
                  </DashboardTableHeader>
                  <DashboardTableBody>
                    {PREVIEW_ROWS.map((row, idx) => (
                      <DashboardTableRow
                        key={`${row.consent}-${idx}`}
                        className="!h-[48px] sm:!h-[48px] !cursor-default hover:!bg-transparent"
                      >
                        <td className="py-3 px-3 text-[13px] font-medium text-[#1F2241]">
                          {row.name}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.email}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.phone}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          <TableChip tone={getConsentChipTone(row.consent)}>
                            {formatConsentStatus(row.consent)}
                          </TableChip>
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.joined}
                        </td>
                      </DashboardTableRow>
                    ))}
                  </DashboardTableBody>
                </DashboardTable>
              </div>

              {/* Single Unified Upload Box */}
              <div className="flex flex-col gap-2.5 shrink-0">
                <h3 className="text-[15px] leading-[22px] font-semibold text-[#1F2241]">
                  Upload File
                </h3>

                <div
                  onClick={openDevicePicker}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative flex-1 border rounded-[8px] px-4 py-4 flex flex-col items-center justify-center bg-white hover:bg-[#FAFAFA] transition-all cursor-pointer ${
                    isDragging
                      ? 'border-[#50AB3D] bg-[#F5FAF4] scale-[1.01]'
                      : hasSelection
                        ? 'border-[#50AB3D]/60 bg-[#FAFCF9]'
                        : 'border-[#D0D5D1]'
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload file"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDevicePicker();
                    }
                  }}
                >
                  {/* Hidden Native File Input for Device Choice */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={handleFileChange}
                  />

                  <div className="flex flex-col items-center gap-1.5 text-center pointer-events-none w-full">
                    <div className="w-8 h-8 rounded-[6px] bg-[#EBF4EA] flex items-center justify-center text-[#50AB3D]">
                      {uploadSource === 'google_drive' ? (
                        <GoogleSheetsIcon className="w-4 h-4" />
                      ) : (
                        <FileDocIcon className="w-4 h-4" />
                      )}
                    </div>

                    {file ? (
                      <div className="flex items-center gap-2 max-w-full px-2">
                        <span className="text-[14px] font-medium text-[#1F2241] truncate max-w-[280px]">
                          {file.name}
                        </span>
                        <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                          Device
                        </span>
                        <button
                          type="button"
                          onClick={clearSelection}
                          className="pointer-events-auto p-1 text-gray-400 hover:text-red-500 transition-colors ml-1"
                          title="Remove file"
                        >
                          ✕
                        </button>
                      </div>
                    ) : selectedSheet ? (
                      <div className="flex flex-col items-center gap-0.5 max-w-full px-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[14px] font-semibold text-[#1F2241] truncate max-w-[260px]">
                            {selectedSheet.name}
                          </span>
                          <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full shrink-0">
                            Google Drive
                          </span>
                          <button
                            type="button"
                            onClick={clearSelection}
                            className="pointer-events-auto p-1 text-gray-400 hover:text-red-500 transition-colors ml-1"
                            title="Remove sheet"
                          >
                            ✕
                          </button>
                        </div>
                        <span className="text-[11px] text-gray-500 font-medium">
                          Tab: {sheetName || 'Sheet1'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <p className="text-[14px] leading-[18px]">
                          <span className="font-semibold text-[#50AB3D]">
                            Click To Upload
                          </span>{' '}
                          <span className="text-[#868686]">
                            or drag and drop
                          </span>
                        </p>
                        <p className="text-[12px] leading-[16px] text-[#868686]">
                          XLSX, XLS or CSV
                        </p>
                      </>
                    )}
                  </div>
                  {isGwConnected && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrivePicker();
                      }}
                      disabled={isPickerLoading || isConnectingGoogle}
                      className="absolute top-2.5 right-2.5 z-10 inline-flex w-10 h-10 items-center justify-center rounded-[8px] border border-[#D0D5D1] bg-white hover:bg-[#FAFAFA] hover:border-[#50AB3D] shadow-xs transition-colors disabled:opacity-60"
                      aria-label={
                        isGwConnected
                          ? 'Import from Google Drive'
                          : 'Connect Google Drive'
                      }
                      title={
                        isPickerLoading
                          ? 'Opening Google Drive'
                          : isGwConnected
                            ? 'Google Drive'
                            : 'Connect Google Drive'
                      }
                    >
                      <GoogleDriveIcon className="w-6 h-6" />
                    </button>
                  )}
                </div>
              </div>

              {sheetPreview && selectedSheet?.id && (
                <div className="p-4 bg-white border border-[#50AB3D]/30 rounded-2xl flex flex-col gap-3">
                  <h4 className="text-[14px] font-semibold text-[#1F2241]">
                    Selected Google Sheet
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <span className="text-[11px] font-medium text-gray-500">
                        File Name
                      </span>
                      <p className="text-[13px] font-semibold text-gray-900 mt-0.5 break-all">
                        {sheetPreview.fileName}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <span className="text-[11px] font-medium text-gray-500">
                        Sheet / Tab Name
                      </span>
                      <p className="text-[13px] font-semibold text-gray-900 mt-0.5">
                        {sheetPreview.sheetName}
                      </p>
                    </div>
                  </div>
                  {Array.isArray(sheetPreview.rows) &&
                    sheetPreview.rows.length > 0 && (
                      <p className="text-[12px] text-gray-500">
                        {sheetPreview.rows.length} preview rows loaded.
                      </p>
                    )}
                  {!sheetPreview.rows && (
                    <p className="text-[12px] text-gray-500">
                      Row preview is not returned by Google Picker. Import
                      Contacts writes this sheet only after you confirm.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>

      <ImportSummaryModal
        result={fileImportSummary}
        onDone={() => setFileImportSummary(null)}
      />
    </>
  );
}

function normalizeImportSummary(data) {
  const failedFromErrors = Array.isArray(data?.errors) ? data.errors.length : 0;
  return {
    total_rows: Number(data?.total_rows ?? data?.total_records ?? 0),
    created_count: Number(data?.created_count ?? 0),
    updated_count: Number(data?.updated_count ?? 0),
    duplicate_count: Number(data?.duplicate_count ?? 0),
    failed_count: Number(data?.failed_count ?? failedFromErrors),
  };
}

const SUMMARY_CARDS = [
  {
    key: 'total_rows',
    label: 'Total Records Processed',
    tone: 'bg-gray-50 text-gray-900',
    labelTone: 'text-gray-500',
  },
  {
    key: 'created_count',
    label: 'New Records Created',
    mark: '✓',
    tone: 'bg-green-50 text-green-700',
    labelTone: 'text-green-700',
  },
  {
    key: 'updated_count',
    label: 'Existing Records Updated',
    mark: '↻',
    tone: 'bg-blue-50 text-blue-700',
    labelTone: 'text-blue-700',
  },
  {
    key: 'duplicate_count',
    label: 'Duplicate Records Skipped',
    mark: '☰',
    tone: 'bg-amber-50 text-amber-800',
    labelTone: 'text-amber-800',
  },
  {
    key: 'failed_count',
    label: 'Failed Records',
    mark: '✕',
    tone: 'bg-red-50 text-red-700',
    labelTone: 'text-red-700',
  },
];

function ImportSummaryModal({ result, onDone }) {
  if (!result) return null;

  const cards = SUMMARY_CARDS;

  return (
    <Modal
      isOpen
      onClose={onDone}
      showClose={false}
      size="lg"
      zIndexClass="z-[80]"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onDone}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-primary text-white text-[14px] font-semibold hover:bg-primary-hover transition-colors"
          >
            Done
          </button>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center gap-2 pb-4 border-b border-gray-100">
        <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-[22px] font-bold">
          ✓
        </div>
        <h3 className="text-[18px] sm:text-[20px] font-semibold text-[#1F2241]">
          Import Completed Successfully
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
        {cards.map((card) => (
          <div key={card.key} className={`p-3.5 rounded-xl ${card.tone}`}>
            <span className={`text-[12px] font-medium ${card.labelTone}`}>
              {card.mark ? `${card.mark} ` : ''}
              {card.label}
            </span>
            <p className="text-[22px] font-bold mt-1">
              {Number(result[card.key] ?? 0)}
            </p>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default CRMImportWizard;
